import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronDown, ShoppingCart, User, Menu, Search as SearchIcon, Globe, LogOut, Package, Settings, Wallet, Coins } from "lucide-react";
import { Button } from "@/components/ui/button";
import { MegaMenu } from "./MegaMenu";
import { MegaMenuSimple } from "./MegaMenuSimple";
import { MobileBottomNav } from "./MobileBottomNav";
import { MobileMenu } from "./MobileMenu";
import { CartDrawer } from "@/components/cart/CartDrawer";
import { AuthDrawer } from "@/components/auth/AuthDrawer";
import { SearchOverlay } from "@/components/ui/search-overlay";
import { LanguageCurrencyModal } from "@/components/ui/language-currency-modal";
import { useAuth } from "@/contexts/AuthContext";
import { useCart } from "@/contexts/CartContext";
import { cn } from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import mosaicLogo from "@/assets/mosaic-logo.svg";

const menuCategories = [
  { id: "1", name: "Oblečenie", slug: "oblecenie", children: [
    { id: "1a", name: "Tričká", slug: "tricka" },
    { id: "1b", name: "Mikiny", slug: "mikiny" },
  ]},
  { id: "2", name: "Doplnky", slug: "doplnky", children: [
    { id: "2a", name: "Čiapky", slug: "ciapky" },
  ]},
];

const featuredProducts = [
  { id: "1", name: "Demo Product", artist: "Artist", price: 29.99, image: "https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?w=200&h=200&fit=crop" },
];

const topBanners = [
  { id: "1", title: "Zľava 15%", image: "https://images.unsplash.com/photo-1556821840-3a63f95609a7?w=600&h=300&fit=crop", link: "#" },
];

interface NavItem {
  label: string;
  href?: string;
  hasMegaMenu?: boolean;
}

interface NavItem {
  label: string;
  href?: string;
  hasMegaMenu?: boolean;
  megaMenuType?: "full" | "simple";
}

const navItems: NavItem[] = [
  { label: "Hudba", hasMegaMenu: true, megaMenuType: "full" },
  { label: "Oblečenie", hasMegaMenu: true, megaMenuType: "full" },
  { label: "Doplnky", hasMegaMenu: true, megaMenuType: "simple" },
  { label: "Technika", href: "/technika" },
  { label: "% Zľavy", href: "/vypredaj" },
];

const currencies = [
  { code: "EUR", symbol: "€", flag: "🇸🇰" },
  { code: "CZK", symbol: "Kč", flag: "🇨🇿" },
  { code: "HUF", symbol: "Ft", flag: "🇭🇺" },
  { code: "PLN", symbol: "zł", flag: "🇵🇱" },
];

const languages = [
  { code: "SK", name: "Slovenčina", flag: "🇸🇰" },
  { code: "CZ", name: "Čeština", flag: "🇨🇿" },
  { code: "EN", name: "English", flag: "🇬🇧" },
];

// Mobile category tree for drawer
const mobileCategories = [
  { id: "1", name: "Oblečenie", slug: "oblecenie", icon: "👕", children: [
    { id: "1a", name: "Tričká", slug: "tricka", children: [] },
    { id: "1b", name: "Mikiny", slug: "mikiny", children: [] },
    { id: "1c", name: "Bundy", slug: "bundy", children: [] },
    { id: "1d", name: "Nohavice", slug: "nohavice", children: [] },
  ]},
  { id: "2", name: "Doplnky", slug: "doplnky", icon: "🎒", children: [
    { id: "2a", name: "Čiapky", slug: "ciapky", children: [] },
    { id: "2b", name: "Tašky", slug: "tasky", children: [] },
  ]},
  { id: "3", name: "Hudba", slug: "hudba", icon: "🎵", children: [
    { id: "3a", name: "CD", slug: "cd", children: [] },
    { id: "3b", name: "LP / Vinyl", slug: "lp", children: [] },
  ]},
  { id: "4", name: "Technika", slug: "technika", icon: "📱", children: [] },
  { id: "5", name: "Poukážky", slug: "poukazky", icon: "🎁", children: [] },
];

export const MainNavigation = () => {
  const navigate = useNavigate();
  const { isLoggedIn, user, logout } = useAuth();
  const { itemCount, isAnimating } = useCart();
  const [megaMenuOpen, setMegaMenuOpen] = useState<string | null>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isAuthOpen, setIsAuthOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isRegionModalOpen, setIsRegionModalOpen] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState(currencies[0]);
  const [selectedLanguage, setSelectedLanguage] = useState(languages[0]);
  
  // Fallback user data
  const currentUser = user || {
    name: "Používateľ",
    email: "",
    initials: "U",
    hasBudget: false,
    budget: 0,
    hasCredit: false,
    credit: 0,
  };

  const handleSearchSubmit = (query: string) => {
    navigate(`/vyhladavanie?q=${encodeURIComponent(query)}`);
  };

  return (
    <>
      {/* Mega Menu backdrop and content - rendered first, below header */}
      {megaMenuOpen !== null && (
        <div 
          className="fixed inset-0 bg-foreground/20 backdrop-blur-sm z-[80]"
          onClick={() => setMegaMenuOpen(null)}
        />
      )}

      <header className="sticky top-0 z-[100] bg-background border-b border-border">
        <div className="max-w-[1600px] mx-auto px-4 lg:px-6">
          {/* Desktop Header */}
          <div className="h-16 hidden lg:flex items-center gap-6">
            {/* Logo */}
            <Link to="/" className="flex-shrink-0 -ml-2">
              <img src={mosaicLogo} alt="Mosaic" className="h-14 w-auto" />
            </Link>

            {/* Navigation */}
            <nav className="hidden lg:flex items-center gap-1 flex-1">
              {navItems.map((item) => (
                <div key={item.label}>
                  {item.hasMegaMenu ? (
                    <button
                      onClick={() => setMegaMenuOpen(megaMenuOpen === item.label ? null : item.label)}
                      className={`flex items-center gap-1 px-3 py-2 text-sm font-bold transition-colors ${
                        megaMenuOpen === item.label
                          ? "text-primary"
                          : item.label === "% Zľavy"
                            ? "text-destructive"
                            : "text-foreground hover:text-primary"
                      }`}
                    >
                      {item.label}
                      <ChevronDown className={`w-4 h-4 transition-transform ${megaMenuOpen === item.label ? "rotate-180" : ""}`} />
                    </button>
                  ) : (
                    <Link
                      to={item.href || "#"}
                      className={`px-3 py-2 text-sm font-bold transition-colors ${
                        item.label === "% Zľavy" ? "text-destructive" : "text-foreground hover:text-primary"
                      }`}
                    >
                      {item.label}
                    </Link>
                  )}
                </div>
              ))}
            </nav>

            {/* Search */}
            <button
              onClick={() => setIsSearchOpen(true)}
              className="hidden lg:flex items-center gap-2 flex-1 max-w-md px-4 py-2 rounded-lg bg-secondary text-muted-foreground text-sm hover:bg-secondary/80 transition-colors"
            >
              <SearchIcon className="w-4 h-4" />
              <span>Hľadať produkty...</span>
              <kbd className="ml-auto text-[10px] bg-background px-1.5 py-0.5 rounded border border-border">⌘K</kbd>
            </button>

            {/* Actions */}
            <div className="flex items-center gap-1">
              {/* Language & Currency - Combined */}
              <Button 
                variant="ghost" 
                size="sm" 
                className="hidden md:flex gap-2 text-xs px-3"
                onClick={() => setIsRegionModalOpen(true)}
              >
                <Globe className="w-4 h-4" />
                <span>{selectedLanguage.flag} {selectedCurrency.symbol}</span>
              </Button>

              <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setIsSearchOpen(true)}>
                <SearchIcon className="w-5 h-5" />
              </Button>
              
              {/* User Menu - Logged In vs Logged Out */}
              {isLoggedIn ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="flex items-center gap-2 px-2 py-1.5 rounded-xl hover:bg-secondary transition-colors">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-primary/60 flex items-center justify-center text-primary-foreground text-xs font-bold">
                        {currentUser.initials}
                      </div>
                      <div className="hidden md:block text-left">
                        <p className="text-xs font-medium text-foreground leading-tight">{currentUser.name.split(" ")[0]}</p>
                        <div className="flex items-center gap-1.5 leading-tight">
                          {currentUser.hasBudget && (
                            <span className="text-[10px] text-primary font-bold flex items-center gap-0.5">
                              <Wallet className="w-2.5 h-2.5" />
                              {currentUser.budget.toFixed(2).replace(".", ",")} €
                            </span>
                          )}
                          {currentUser.hasCredit && (
                            <span className="text-[10px] text-[#009FDB] font-bold flex items-center gap-0.5">
                              <Coins className="w-2.5 h-2.5" />
                              {currentUser.credit.toFixed(2).replace(".", ",")} €
                            </span>
                          )}
                        </div>
                      </div>
                      <ChevronDown className="w-3.5 h-3.5 text-muted-foreground hidden md:block" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56 bg-background z-[200]">
                    <div className="px-3 py-2 border-b border-border">
                      <p className="font-medium text-foreground">{currentUser.name}</p>
                      <p className="text-xs text-muted-foreground">{currentUser.email}</p>
                      {currentUser.hasBudget && (
                        <div className="flex items-center gap-1.5 mt-1.5 px-2 py-1 rounded-md bg-primary/10">
                          <Wallet className="w-3.5 h-3.5 text-primary" />
                          <span className="text-sm font-bold text-primary">{currentUser.budget.toFixed(2).replace(".", ",")} €</span>
                          <span className="text-xs text-muted-foreground">budget</span>
                        </div>
                      )}
                      {currentUser.hasCredit && (
                        <div className="flex items-center gap-1.5 mt-1.5 px-2 py-1 rounded-md bg-[#009FDB]/10">
                          <Coins className="w-3.5 h-3.5 text-[#009FDB]" />
                          <span className="text-sm font-bold text-[#009FDB]">{currentUser.credit.toFixed(2).replace(".", ",")} €</span>
                          <span className="text-xs text-muted-foreground">credit</span>
                        </div>
                      )}
                    </div>
                    <DropdownMenuItem asChild>
                      <Link to="/profil" className="flex items-center gap-2 cursor-pointer">
                        <Package className="w-4 h-4" />
                        Moje objednávky
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/profil?tab=billing" className="flex items-center gap-2 cursor-pointer">
                        <Settings className="w-4 h-4" />
                        Nastavenia účtu
                      </Link>
                    </DropdownMenuItem>
                    {currentUser.hasBudget && (
                      <DropdownMenuItem asChild>
                        <Link to="/profil?tab=budget" className="flex items-center gap-2 cursor-pointer">
                          <Wallet className="w-4 h-4" />
                          Budget
                        </Link>
                      </DropdownMenuItem>
                    )}
                    {currentUser.hasCredit && (
                      <DropdownMenuItem asChild>
                        <Link to="/profil?tab=credit" className="flex items-center gap-2 cursor-pointer">
                          <Coins className="w-4 h-4" />
                          Credit
                        </Link>
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem 
                      className="text-destructive focus:text-destructive cursor-pointer"
                      onClick={logout}
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Odhlásiť sa
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Button variant="ghost" size="icon" onClick={() => setIsAuthOpen(true)}>
                  <User className="w-5 h-5" />
                </Button>
              )}
              
              <Button variant="ghost" size="sm" className="gap-2" onClick={() => setIsCartOpen(true)}>
                <ShoppingCart className={cn("w-5 h-5 transition-transform", isAnimating && "cart-bounce")} />
                <span className={cn(
                  "w-5 h-5 rounded-full bg-primary text-primary-foreground text-xs font-bold flex items-center justify-center",
                  isAnimating && "cart-badge-pop"
                )}>
                  {itemCount}
                </span>
              </Button>
            </div>
          </div>

          {/* Mobile Header */}
          <div className="h-14 flex lg:hidden items-center justify-between">
            {/* Hamburger Menu - Left */}
            <button 
              onClick={() => setIsMobileMenuOpen(true)}
              className="w-10 h-10 flex items-center justify-center rounded-xl hover:bg-secondary transition-colors -ml-2"
            >
              <Menu className="w-6 h-6" />
            </button>

            {/* Logo - Center */}
            <Link to="/" className="absolute left-1/2 -translate-x-1/2">
              <img src={mosaicLogo} alt="Mosaic" className="h-8 w-auto" />
            </Link>

            {/* Right Actions - User & Cart only (search is in menu and bottom nav) */}
            <div className="flex items-center gap-0.5">
              {isLoggedIn ? (
                <Link to="/profil" className="w-10 h-10 flex items-center justify-center">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-primary/60 flex items-center justify-center text-primary-foreground text-xs font-bold">
                    {currentUser.initials}
                  </div>
                </Link>
              ) : (
                <button 
                  onClick={() => setIsAuthOpen(true)}
                  className="w-10 h-10 flex items-center justify-center"
                >
                  <User className="w-5 h-5" />
                </button>
              )}
              <button 
                onClick={() => setIsCartOpen(true)}
                className="w-10 h-10 flex items-center justify-center relative"
              >
                <ShoppingCart className={cn("w-5 h-5 transition-transform", isAnimating && "cart-bounce")} />
                <span className={cn(
                  "absolute top-1 right-1 w-4 h-4 rounded-full bg-primary text-primary-foreground text-[10px] font-bold flex items-center justify-center",
                  isAnimating && "cart-badge-pop"
                )}>
                  {itemCount}
                </span>
              </button>
            </div>
          </div>
        </div>

        {/* MegaMenu - inside header, positioned below */}
        {megaMenuOpen && navItems.find(item => item.label === megaMenuOpen)?.megaMenuType === "simple" ? (
          <MegaMenuSimple
            isOpen={megaMenuOpen !== null}
            onClose={() => setMegaMenuOpen(null)}
            categories={[]}
            featuredProducts={featuredProducts}
          />
        ) : (
          <MegaMenu
            isOpen={megaMenuOpen !== null}
            onClose={() => setMegaMenuOpen(null)}
            categories={menuCategories}
            featuredProducts={featuredProducts}
            topBanners={topBanners}
          />
        )}
      </header>

      <CartDrawer isOpen={isCartOpen} onClose={() => setIsCartOpen(false)} />
      <AuthDrawer isOpen={isAuthOpen} onClose={() => setIsAuthOpen(false)} />
      <SearchOverlay isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} />
      <MobileMenu 
        isOpen={isMobileMenuOpen} 
        onClose={() => setIsMobileMenuOpen(false)} 
        onSearchClick={() => setIsSearchOpen(true)}
        categories={mobileCategories}
      />
      <LanguageCurrencyModal
        isOpen={isRegionModalOpen}
        onClose={() => setIsRegionModalOpen(false)}
        currencies={currencies}
        languages={languages}
        selectedCurrency={selectedCurrency}
        selectedLanguage={selectedLanguage}
        onCurrencyChange={setSelectedCurrency}
        onLanguageChange={setSelectedLanguage}
      />

      {/* Mobile Bottom Navigation */}
      <MobileBottomNav
        onSearchClick={() => setIsSearchOpen(true)}
        onCartClick={() => setIsCartOpen(true)}
        onAuthClick={() => setIsAuthOpen(true)}
        onCategoriesClick={() => setIsMobileMenuOpen(true)}
        cartCount={2}
        isLoggedIn={isLoggedIn}
      />
    </>
  );
};
